import React, { useState } from 'react';
import BodyFrameSvg from './BodyFrameSvg';
import PainScalePopup from './PainScalePopup';
import { ThreeMonthPlanItem } from '../../PersonalisedPlan/service/threeMonthPlanService';

interface BodyFrameComponentProps {
  gender: string;
  planItems: ThreeMonthPlanItem[];
}

const fmt = (val: string) => val || '-';
const fmtScore = (score: number) => (score === 0 ? '-' : score);

const toDisplayWeightInLbs = (val: string): string => {
  if (!val) return '-';
  if (val.toLowerCase().includes('pound') || val.toLowerCase().includes('lb')) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return `${Math.round(num * 2.20462)} pounds`;
};

const getScoreStyle = (score: number) => {
  if (score <= 20)
    return {
      bg: 'bg-[#FFE2E2] border-[1px] border-[#ECD6D6]',
      label: 'text-[#CB3737]',
      value: 'text-[#CB3737]'
    };
  if (score <= 40)
    return {
      bg: 'bg-[#FFF4E6] border-[1px] border-[#EFDABD]',
      label: 'text-[#D7742D]',
      value: 'text-[#D7742D]'
    };
  if (score <= 60)
    return {
      bg: 'bg-[#FFF5CF] border-[1px] border-[#D6CCAC]',
      label: 'text-[#713F12]',
      value: 'text-[#713F12]'
    };
  if (score <= 80)
    return {
      bg: 'bg-[#EAFFE6] border-[1px] border-[#CFE0CB]',
      label: 'text-[#32B66F]',
      value: 'text-[#32B66F]'
    };
  return {
    bg: 'bg-[#EBF7EC] border-[1px] border-[#C0DCC2]',
    label: 'text-[#04471B]',
    value: 'text-[#04471B]'
  };
};

const BodyFrameComponent = ({ gender, planItems }: BodyFrameComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOnClose = () => {
    setIsOpen(false);
  };

  const handleOpenPopup = () => {
    setIsOpen(true);
  };

  return (
    <div className="w-full bg-[#fafbfc] flex justify-center items-start pt-[30px] md:pt-[50px] border-[#F2F2F2]">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-sm flex flex-col gap-[25px]">
        <div className="px-[25px] pt-[25px] flex flex-col gap-[30px]">
          <div className="flex flex-col min-[900px]:flex-row gap-[30px]">
            {planItems.map((item, index) => {
              const scoreStyle = getScoreStyle(item.flexibility_score);
              return (
                <div
                  key={index}
                  className="flex-1 bg-[#f8f9fa] rounded-xl flex flex-col items-center"
                >
                  <div className="p-[20px] w-full flex flex-col items-center justify-start relative">
                    <div className="text-[14.5px] font-[550] leading-normal font-Inter text-[#000] mb-1">
                      {item.timeline}
                    </div>
                    <div className="text-[12px] font-medium leading-normal font-Inter text-[#868686] mb-4">
                      {item.timeline === 'Today'
                        ? 'Your Current Physical Health'
                        : 'Projected Physical Health'}
                    </div>
                    <div className="mb-4 flex items-center justify-center">
                      <BodyFrameSvg gender={gender} planItem={item} />
                    </div>
                    <div className="absolute bottom-0 left-[18px] flex flex-col gap-[10px] mb-[21px]">
                      <div className="flex flex-col justify-start items-start text-left text-[11.5px] leading-[18px] font-Inter">
                        <span className="font-[444] text-[#9A9A9A]">Weight:</span>
                        <span className="font-[560] text-[#090909]">
                          {toDisplayWeightInLbs(item.weight)}
                        </span>
                      </div>
                      <div className="flex flex-col justify-start items-start text-left text-[11.5px] leading-[18px] font-Inter">
                        <span className="font-[444] text-[#9A9A9A]">BMI:</span>
                        <span className="font-[560] text-[#090909]">{fmt(item.bmi)}</span>
                      </div>
                      <div className="flex flex-col justify-start items-start text-left text-[11.5px] leading-[18px] font-Inter">
                        <span className="font-[444] text-[#9A9A9A]">Stress level:</span>
                        <span className="font-[560] text-[#090909]">{fmt(item.stress_level)}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`w-full mt-auto rounded-b-xl px-[18px] py-[15px] flex justify-between items-center ${scoreStyle.bg}`}
                  >
                    <span
                      className={`text-[11.5px] font-medium leading-[20px] font-Inter ${scoreStyle.label}`}
                    >
                      {item.timeline === 'Today'
                        ? 'Flexibility score:'
                        : 'Projected flexibility score:'}
                    </span>
                    <span
                      className={`text-[11.5px] font-medium leading-[20px] font-Inter ${scoreStyle.value}`}
                    >
                      {fmtScore(item.flexibility_score)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pain Descriptions */}
          {/* <div className="text-[13.5px] md:text-[14.5px] leading-[22px] font-Inter flex flex-col gap-4 justify-start items-start text-left">
                        <span className='font-medium text-[#4c4c4c]'><span className="font-semibold text-[#000]">Back Pain</span> - To help remedy your back pain, we'll use yoga poses like Cat-Cow, Child's Pose, and Downward Dog to gently stretch and strengthen your spine.</span>
                        <span className='font-medium text-[#4c4c4c]'><span className="font-semibold text-[#000]">Shoulder Pain</span> - To help remedy your left shoulder pain, we'll use yoga poses like Thread the Needle, Eagle Arms, and Cow Face Arms to release tension and improve mobility.</span>
                    </div> */}
        </div>

        {/* Footer - now absolute and stretched */}
        <div className="w-full bg-white border-t border-gray-100 px-[20px] md:px-[25px] py-[14px] md:py-[20px] rounded-b-2xl flex justify-start items-center gap-1 text-[13px] font-medium leading-[16px] md:leading-[22px] font-Inter">
          <span className="text-[#8d8d8d]">
            Understanding our pain colour scales.{' '}
            <span onClick={handleOpenPopup} className="font-semibold text-[#4c4c4c] cursor-pointer">
              Learn more
            </span>
          </span>
        </div>
      </div>
      {isOpen && <PainScalePopup onCloseIconClicked={handleOnClose} isOpen={isOpen} />}
    </div>
  );
};
export default BodyFrameComponent;
